import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetOrganizations, dataSetContactInfos, dataSetTumCredentialsList } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { IOrganization } from '../../../@types/model/masterData/organization/organization';
import OrganizationHttpService from '../../../services/http/masterData/organizationHttpService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import { IOrganizationFormValues } from '../../../@types/model/masterData/organization/organizationFormValues';
import OrganizationForm from './form/OrganizationForm';
import { ConfirmationDialog, ProjectDialog } from '@zz2/zz2-ui';
import { IApiError } from '../../../@types/other';
import ContactInfoHttpService from '../../../services/http/masterData/contactInfoHttpService';
import { setOrganizationMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { useLocation } from 'react-router-dom';
import OrganizationModelHelper from '../../../@types/model/masterData/organization/organizationModelHelper';
import UserHelper from '../../../services/helper/userHelper';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';
import TumCredentialsHttpService from '../../../services/http/masterData/tumCredentialsHttpService';
import { CustomTable } from '../../../components/table/CustomTableWrapper';

const OrganizationScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const organizations = useAppSelector(x => x.masterData.organizations);
    const contactInfos = useAppSelector(x => x.masterData.contactInfos);
    const tumCredentialsList = useAppSelector(x => x.masterData.tumCredentialsList);

    const [isLoading, setIsLoading] = useState(false);
    const [selectedOrganization, setSelectedOrganization] = useState<IOrganization | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [deletingOrganization, setDeletingOrganization] = useState<IOrganization | null>(null);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        setIsLoading(true);

        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const orgResult = await OrganizationHttpService.getOrganizationData();
                const contactInfoResult = await ContactInfoHttpService.getContactInfoData();
                dispatch(dataSetOrganizations(orgResult.data));
                dispatch(dataSetContactInfos(contactInfoResult.data));
            } catch (error : unknown) {
                const apiError = error as IApiError | undefined;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading organizations.');
            }
        }

        try {
            const tumCredsResult = await TumCredentialsHttpService.getList([]);
            dispatch(dataSetTumCredentialsList(tumCredsResult.data));
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading organizations.');
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = async (values : IOrganizationFormValues) => {
        setIsLoading(true);

        try {
            const upsert = OrganizationModelHelper.createUpsert(values);
            const res = await OrganizationHttpService.addOrUpdateOrganization(upsert);
            const updatedList = upsertArrayElement(organizations, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetOrganizations(updatedList));
            await setOrganizationMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedOrganization ? 'Organization updated successfully.' : 'Organization added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating organization data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeOrganization = async () => {
        if (!deletingOrganization) return;
        const updatedOrganization = { ...deletingOrganization, isActive: false };
        setIsLoading(true);
        try {
            const res = await OrganizationHttpService.deleteOrganization(updatedOrganization.id);
            const updatedList = upsertArrayElement(organizations, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetOrganizations(updatedList));
            await setOrganizationMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Organization deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting organization.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedOrganization(null);
    };

    const edit = (selectedRow : IOrganization) => {
        setSelectedOrganization(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : IOrganization) : void => {
        setDeletingOrganization(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingOrganization(null);
    };

    const deleteButtonTooltip = (row : IOrganization) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Organization', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    const formatContactInfos = (contactInfoIds : Array<number>) => {
        return contactInfos
            .filter(x => contactInfoIds.includes(x.id))
            .map(x => x.name)
            .toString()
            .replace(/,/g, ', ');
    };

    const getTumCredentialsName = (tumCredentialsId : number) => {
        return tumCredentialsList.find(x => x.id === tumCredentialsId)?.name ?? '';
    };


    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return OrganizationModelHelper.createFormValues(
                selectedOrganization,
                contactInfos,
                tumCredentialsList
            );
        }
    }, [
        selectedOrganization,
        isDialogOpen,
        contactInfos,
        tumCredentialsList
    ]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            <div className={'fdc hfill'}>
                <Card className={'fdc hfill'}>
                    <CustomTable<IOrganization>
                    
                        enableAdding
                        // addTooltipRightValidation={!userHasRights.addRight}
                        disableAddButton={!userHasRights.addRight || isLoading}
                        addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('Organization', RightGrantsEnum.Add) : 'Add'}
                        addFunction={openModal}
                        // addTooltipOnClick={() : void => requestAccess('Add')}
                    
                        enableEditing
                        // editTooltipRightValidation={!userHasRights.editRight}
                        disableEditButton={!userHasRights.editRight || isLoading}
                        editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Organization', RightGrantsEnum.Edit) : 'Edit'}
                        editFunction={edit}
                        // editTooltipOnClick={() : void => requestAccess('Edit')}

                        enableDeleting
                        // deleteTooltipRightValidation={!userHasRights.deleteRight}
                        disableDeleteButton={(row : IOrganization) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                        deleteTooltip={deleteButtonTooltip}
                        deleteFunction={onDelete}
                        // deleteTooltipOnClick={() : void => requestAccess('Delete')}

                        enableSorting
                        enableFiltering
                        enablePagination
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, width: 100, enableSorting: true },
                            { title: 'Name', field: 'name', enableFiltering: true, width: 250, enableSorting: true },
                            { title: 'Supplier?', field: 'isSupplier', type: 'boolean', width: 100, enableFiltering: true, enableSorting: true },
                            { title: 'Client?', field: 'isClient', type: 'boolean', width: 100, enableFiltering: true, enableSorting: true },
                            { title: 'Can Intake?', field: 'canIntake', type: 'boolean', width: 100, enableFiltering: true, enableSorting: true },
                            { title: 'Requires Project?', field: 'requiresProject', type: 'boolean', width: 150, enableFiltering: true, enableSorting: true },
                            { title: 'Exporter Code', field: 'exporter', enableFiltering: true, enableSorting: true },
                            { title: 'Billing Party', field: 'billingParty', enableFiltering: true, enableSorting: true },
                            { title: 'TUM Business Id', field: 'tumBusinessId', enableFiltering: true, enableSorting: true },
                            { title: 'TUM Logistical Provider Id', field: 'tumLogisticalProviderId', enableFiltering: true, enableSorting: true },
                            { title: 'TUM Credentials', field: 'tumCredentialsId', formatFunction: getTumCredentialsName, enableFiltering: true, enableSorting: true },
                            { title: 'Waybill Prefix', field: 'waybillPrefix', enableFiltering: true, enableSorting: true },
                            { title: 'Next Waybill Nr', field: 'nextWaybillNumber', enableFiltering: true, enableSorting: true },
                            { title: 'Waybill Range End', field: 'waybillRangeEnd', enableFiltering: true, enableSorting: true },
                            { title: 'ContactInfos', field: 'contactInfoIds', formatFunction: formatContactInfos, width: 200, enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={organizations}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedOrganization ? 'Edit Organization' : 'Add Organization'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'md'}
                    fullWidth
                >
                    <OrganizationForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isLoading={isLoading}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Organization'}
                open={!!deletingOrganization}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeOrganization}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default OrganizationScreen;
